import * as React from 'react';

import { cn } from '@/shared/infra/ui/lib/utils';

export type FieldMessageProps = {
  /** Help text. Revealed while the field has focus; its height is reserved either way. */
  description?: React.ReactNode;
  errors?: Array<{ message?: string } | undefined>;
  invalid?: boolean;
  className?: string;
  /** Ids for the two messages, so the field can name them in `aria-describedby`. */
  descriptionId?: string;
  errorId?: string;
};

/**
 * The single help/error line beneath a form field. The space is reserved, not revealed: both
 * messages stay mounted in one grid cell and only their opacity changes, so neither focus nor
 * validation can resize the field. The slot pins its height in pixels and transitions between
 * values for the one legitimate size change — an error taller than the help text.
 * Background & traps: verifyi9-docs/features/frontend-ui/layout-stability-and-motion.md
 */
export function FieldMessage({
  description,
  errors,
  invalid,
  className,
  descriptionId,
  errorId,
}: FieldMessageProps) {
  const slotRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = React.useState<number>();

  const messages = React.useMemo(() => {
    const present = (errors ?? [])
      .map((error) => error?.message)
      .filter((message): message is string => !!message);
    return [...new Set(present)];
  }, [errors]);

  const showError = !!invalid && messages.length > 0;

  React.useLayoutEffect(() => {
    const slot = slotRef.current;
    if (!slot) return;
    const measure = () => {
      const tallest = Math.max(
        0,
        ...[...slot.children].map((child) => (child as HTMLElement).offsetHeight)
      );
      // Ceil so a fractional line-box is never clipped by its own pinned height.
      setHeight((previous) => {
        const next = Math.ceil(tallest);
        return previous === next ? previous : next;
      });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return; // jsdom — tests assert structure, not motion
    const observer = new ResizeObserver(measure);
    for (const child of slot.children) observer.observe(child);
    return () => observer.disconnect();
  }, [description, messages]);

  return (
    <div
      ref={slotRef}
      style={height !== undefined ? { height } : undefined}
      className={cn(
        'grid content-start mt-1 min-h-4 pl-4 pr-2 text-[11px] leading-4 text-left',
        'overflow-hidden transition-[height] duration-200 ease-out motion-reduce:transition-none',
        className
      )}
    >
      {description ? (
        <p
          id={descriptionId}
          className={cn(
            '[grid-area:1/1] text-rm-brand-ink transition-opacity duration-200 ease-out motion-reduce:transition-none',
            showError ? 'opacity-0' : 'opacity-0 group-focus-within/input:opacity-100'
          )}
        >
          {description}
        </p>
      ) : null}
      {/* Always mounted so it can announce: an alert region added to the DOM at the same moment as its
          text is not reliably read out. */}
      <p
        id={errorId}
        role="alert"
        className={cn(
          '[grid-area:1/1] text-rm-danger transition-opacity duration-200 ease-out motion-reduce:transition-none',
          showError ? 'opacity-100' : 'opacity-0'
        )}
      >
        {messages.map((message, index) => (
          <span key={index} className="block">
            {message}
          </span>
        ))}
      </p>
    </div>
  );
}
